/**
 * Générateurs des ITEMS NATIONAUX — 6e, Automatismes, campagne 2026.
 *
 * Un générateur par item : il fabrique des variantes de l'item officiel pour le QCM de suivi (bilan),
 * de MÊME type et de MÊME difficulté, pour mesurer un progrès depuis l'évaluation de septembre :
 *   - même énoncé type, même format (QCM à 4 choix, comme l'item) ;
 *   - même domaine de valeurs que l'item (ex. centaine + centaine reste centaine + centaine) ;
 *   - mêmes mauvaises réponses : chaque distracteur officiel est l'effet d'une erreur, et la variante
 *     recalcule cette même erreur sur ses propres valeurs.
 * La fiche de chaque item est écrite en tête de son générateur (lue sur la CAPTURE de l'item, jamais
 * sur le champ texte de la base, qui a des erreurs de transcription : Q01 y dit « quatre-vingt-quatre »).
 * Choix en texte ou en KaTeX (MathText), jamais en image ; figures en SVG maison.
 * `bonneReponseOfficielle` : la bonne réponse de l'item en base ; le registre ne propose une variante
 * que si elle n'a pas changé (si le ministère change l'item, son générateur ne s'applique plus).
 */
import { randInt, randomChoice, makeQcm, tex, formatNumber } from '../../../utils';
import { enLettres } from '../../lettres';

const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: null, label });
const avecZeros = (n, chiffres) => String(n).padStart(chiffres, '0');

/**
 * Q01 — Écriture en français → écriture décimale.
 * Item : « Le nombre soixante-seize-mille-quatre s'écrit : » ; 76 004 (juste), 76 1004, 60 16 1 004, 16 004.
 * Domaine : A mille Y, avec A de 70 à 79 (« soixante » suivi d'un mot de 10 à 19) et Y de 1 à 9
 *   (la classe des unités n'a que des unités : deux zéros à écrire).
 * Erreurs : « mille-Y » lu 1 00Y collé à A (76 1004) ; mot à mot, 60 puis X puis 1 00Y (60 16 1 004) ;
 *   « soixante » oublié (16 004).
 */
const L01 = 'Écriture en français → écriture décimale';
const genQ01 = () => {
    const x = randInt(10, 19);
    const a = 60 + x;
    const y = randInt(1, 9);
    const n = a * 1000 + y;
    const juste = `${a} ${avecZeros(y, 3)}`;
    const choix = [juste, `${a} 1${avecZeros(y, 3)}`, `60 ${x} 1 ${avecZeros(y, 3)}`, `${x} ${avecZeros(y, 3)}`];
    return makeQcm(choix, juste, `<p>Le nombre ${enLettres(n)} s'écrit :</p>`, opts(L01));
};

/**
 * Q02 — Écriture littérale → écriture décimale.
 * Item : « Quel nombre correspond à soixante-douze millièmes ? » ; 0,072 (juste), 0,720, 72,001, 72,1000.
 * Domaine : N millièmes, N de 21 à 99 écrit en un nombre composé, jamais un multiple de 10 (sinon
 *   l'écriture juste perdrait son dernier chiffre).
 * Erreurs : N placé aux dixièmes et centièmes (0,N0) ; « millième » ajouté comme 0,001 (N,001) ;
 *   « mille » écrit 1000 derrière la virgule (N,1000).
 */
const L02 = 'Écriture littérale → écriture décimale';
const genQ02 = () => {
    const n = randInt(2, 9) * 10 + randInt(1, 9);
    const juste = `0,0${n}`;
    const choix = [juste, `0,${n}0`, `${n},001`, `${n},1000`];
    return makeQcm(choix, juste, `<p>Quel nombre correspond à ${enLettres(n)} millièmes ?</p>`, opts(L02));
};

/**
 * Q03 — Décomposition (fraction décimale) → écriture décimale.
 * Item : « 3 + 2/10 = … » ; 3,2 (juste), 0,32, 32,0, 32,10.
 * Domaine : a + b/10, a de 2 à 9, b de 1 à 9.
 * Erreurs : les deux chiffres après la virgule (0,ab) ; les chiffres collés en entier (ab,0) ; collés
 *   avec le dénominateur (ab,10).
 */
const L03 = 'Décomposition fraction décimale → écriture décimale';
const genQ03 = () => {
    const a = randInt(2, 9);
    const b = randInt(1, 9);
    const juste = `${a},${b}`;
    const choix = [juste, `0,${a}${b}`, `${a}${b},0`, `${a}${b},10`];
    return makeQcm(choix, juste, `<p>${tex(`${a} + \\dfrac{${b}}{10} = \\dots`)}</p>`, opts(L03));
};

// ─── Outils des items Q04 à Q23 : gardes, fractions, figures ───────────────────────────────────

/** Second chemin (REGLES_EQUIPE § 3) : une construction fausse lève une erreur au lieu de partir. */
const garde = (condition, message) => {
    if (!condition) throw new Error(`Item national 6e, ${message}`);
};
/** La valeur d'une écriture décimale française (« 7 548,2 » → 7548.2), pour les gardes. */
const valeur = (s) => Number(String(s).replace(/\s/g, '').replace(',', '.'));
/** Une fraction en KaTeX écrite telle quelle (fracTex réduit 4/1 à 4 ; ici 4/1 est un distracteur). */
const fraction = (num, den) => tex(`\\dfrac{${num}}{${den}}`);

const ENCRE = '#111827';
const VERT = '#15803d';
const POLICE = 'font-family="system-ui,sans-serif"';
/** Arrondi au dixième de pixel : un même tirage donne toujours le même SVG. */
const px = (v) => Math.round(v * 10) / 10;
const trait = (x1, y1, x2, y2, couleur = ENCRE, epaisseur = 1.5, extra = '') =>
    `<line x1="${px(x1)}" y1="${px(y1)}" x2="${px(x2)}" y2="${px(y2)}" stroke="${couleur}" stroke-width="${epaisseur}"${extra}/>`;
const texteSvg = (x, y, contenu, { taille = 15, gras = false, couleur = ENCRE } = {}) =>
    `<text x="${px(x)}" y="${px(y)}" font-size="${taille}"${gras ? ' font-weight="700"' : ''} fill="${couleur}"`
    + ` text-anchor="middle" ${POLICE}>${contenu}</text>`;
const polygone = (points, remplissage, contour, epaisseur = 1.5) =>
    `<polygon points="${points.map(([x, y]) => `${px(x)},${px(y)}`).join(' ')}" fill="${remplissage}"`
    + ` stroke="${contour}" stroke-width="${epaisseur}" stroke-linejoin="round"/>`;
/**
 * L'image de l'énoncé (FORMATS_TANOED § 12) : le SVG en data-URL dans config.imageUrl, avec une
 * description neutre, qui ne donne jamais la réponse.
 */
const figure = (largeur, hauteur, alt, contenu) => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${px(largeur)} ${px(hauteur)}" width="${px(largeur)}"`
        + ` role="img" aria-label="${alt}">${contenu}</svg>`;
    return { version: 1, elements: [{ type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, alt } }] };
};
const avecFigure = (label, elements) => ({ ...opts(label), elements_additionnels: elements });

/**
 * Q04 — Écriture fractionnaire → écriture décimale.
 * Item : « Quel nombre est égal à la fraction 4/100 ? » ; 0,04 (juste), 0,4, 4, 400.
 * Domaine : a/100, a de 1 à 9 : un seul chiffre, donc un zéro à écrire aux dixièmes. Cela ne fait que
 *   9 variantes : le dénominateur 1 000 est ajouté un tirage sur trois (a/1000 = 0,00a, mêmes erreurs),
 *   mode auto, TRANCHÉ LE 10/10 (on garde).
 * Erreurs : un zéro de moins, a placé aux dixièmes (0,4 = 4/10) ; le numérateur seul, le dénominateur
 *   oublié (4) ; multiplié par 100 au lieu d'être divisé (400).
 */
const L04 = 'Écriture fractionnaire → écriture décimale';
const genQ04 = () => {
    const a = randInt(1, 9);
    const zeros = randomChoice([2, 2, 3]);
    const juste = `0,${'0'.repeat(zeros - 1)}${a}`;
    garde(Math.abs(valeur(juste) * 10 ** zeros - a) < 1e-9, 'Q04 : écriture décimale');
    const choix = [juste, `0,${'0'.repeat(zeros - 2)}${a}`, String(a), formatNumber(a * 10 ** zeros)];
    return makeQcm(choix, juste, `<p>Quel nombre est égal à la fraction ${fraction(a, 10 ** zeros)} ?</p>`, opts(L04));
};

/**
 * Q05 — Correspondance entre les rangs : centièmes et millièmes.
 * Item : « 5 centièmes est égal à… » ; 10 fois 5 millièmes. (juste), 10 fois 5 dixièmes.,
 *   10 fois 5 unités., 10 fois 5 dizaines.
 * Domaine : N centièmes = 10 fois N millièmes, N = 5 dans l'item. Un chiffre de 2 à 9 ne donne que
 *   8 variantes : N va de 2 à 19, sauf 10, mode auto, TRANCHÉ LE 10/10 (on garde).
 * Erreurs : le rang voisin pris du mauvais côté, comme « 1 centaine = 10 dizaines » (dixièmes) ; les
 *   centièmes pris pour les centaines, 5 centaines = 10 fois 5 dizaines (dizaines) ; unités : erreur non
 *   identifiée avec certitude, TRANCHÉ LE 10/10 (on garde) (les centièmes pris pour les dizaines, le rang symétrique par
 *   rapport à la virgule : 5 dizaines = 10 fois 5 unités).
 */
const L05 = 'Correspondance entre centièmes et dixièmes';
const genQ05 = () => {
    const n = randomChoice([2, 3, 4, 5, 6, 7, 8, 9, 11, 12, 13, 14, 15, 16, 17, 18, 19]);
    const juste = `10 fois ${n} millièmes.`;
    const choix = [`10 fois ${n} dixièmes.`, juste, `10 fois ${n} unités.`, `10 fois ${n} dizaines.`];
    return makeQcm(choix, juste, `<p>${n} centièmes est égal à…</p>`, opts(L05));
};

/**
 * Q06 — Repérage sur une droite graduée (entiers).
 * Item : « Quel grand nombre faut-il placer dans le cadre vide sur cette droite graduée ? » ; droite
 *   de 2 000 000 à 3 000 000 en dix grands intervalles, chacun partagé en dix petits (un trait moyen au
 *   milieu) ; le cadre vide montre la 6e grande graduation ; 2 600 000 (juste), 2 060 000, 2 000 600,
 *   2 000 006.
 * Domaine : de N millions à N + 1 millions, N de 1 à 8 ; le cadre sur la k-ième grande graduation,
 *   k de 1 à 9 (une grande graduation vaut 100 000, une petite 10 000).
 * Erreurs : la grande graduation lue comme une petite, 6 × 10 000 (2 060 000) ; lue comme 100
 *   (2 000 600) ; lue comme 1, les six graduations ajoutées une à une (2 000 006).
 */
const L06 = 'Repérage sur une droite graduée (entiers)';
const fleche = (x, yDepart, yPointe) => {
    const sens = Math.sign(yPointe - yDepart);
    return trait(x, yDepart, x, yPointe - sens * 8)
        + polygone([[x - 5, yPointe - sens * 10], [x + 5, yPointe - sens * 10], [x, yPointe]], ENCRE, ENCRE, 1);
};
const cadre = (x, y, contenu) => `<rect x="${px(x - 56)}" y="${y}" width="112" height="30" rx="3" fill="#ffffff"`
    + ` stroke="${ENCRE}" stroke-width="1.5"/>${contenu ? texteSvg(x, y + 20, contenu, { taille: 14 }) : ''}`;
const figureQ06 = (n, k) => {
    const yAxe = 112;
    const xDe = (i) => 76 + i * 5.6;                    // i petits intervalles de 10 000 après N millions
    let c = trait(xDe(-3), yAxe, xDe(103), yAxe);
    for (let i = -2; i <= 102; i += 1) {
        const dansUnite = i >= 0 && i <= 100;
        let hauteur = 9;
        if (dansUnite && i % 10 === 5) hauteur = 15;
        if (dansUnite && i % 10 === 0) hauteur = 24;
        c += trait(xDe(i), yAxe, xDe(i), yAxe - hauteur, ENCRE, hauteur === 24 ? 3 : 1);
    }
    const bas = formatNumber(n * 1000000);
    const haut = formatNumber((n + 1) * 1000000);
    c += cadre(xDe(10 * k), 12, '') + fleche(xDe(10 * k), 42, yAxe - 27)
        + cadre(xDe(0), 140, bas) + fleche(xDe(0), 140, yAxe + 3)
        + cadre(xDe(100), 140, haut) + fleche(xDe(100), 140, yAxe + 3);
    return figure(712, 178, `Droite graduée de ${bas} à ${haut}, avec un cadre vide relié à une graduation.`, c);
};
const genQ06 = () => {
    const n = randInt(1, 8);
    const k = randInt(1, 9);
    const base = n * 1000000;
    const juste = formatNumber(base + k * 100000);
    const choix = [juste, formatNumber(base + k * 10000), formatNumber(base + k * 100), formatNumber(base + k)];
    return makeQcm(choix, juste,
        '<p>Quel grand nombre faut-il placer dans le cadre vide sur cette droite graduée ?</p>{{element:0}}',
        avecFigure(L06, figureQ06(n, k)));
};

/**
 * Q07 — Repérage sur une droite graduée (décimaux).
 * Item : « Observer cette demi-droite graduée. Quelle est l'abscisse du point B ? » ; demi-droite de 0
 *   à 2,5, l'unité partagée en dix, un repère vert toutes les cinq graduations, seuls 0 et 1 écrits,
 *   B sur la 22e graduation ; 2,2 (juste), 1,12, 22, 20,2.
 * Domaine : B = a,b avec a de 2 à 4 et b de 1 à 4 : B entre un repère entier et le repère du demi qui
 *   le suit, comme 2,2, et au-delà de 2 (avant 2, « 1, puis les graduations » serait juste) ; la
 *   demi-droite s'arrête à a,5, comme l'item.
 * Erreurs : 1, puis le nombre de graduations comptées depuis 1 derrière la virgule (1,12) ; chaque
 *   graduation comptée pour 1 depuis 0 (22) ; les graduations jusqu'au repère vert qui précède B, puis
 *   celles qui restent derrière la virgule (20,2).
 */
const L07 = 'Repérage sur une droite graduée (décimaux)';
const figureQ07 = (a, b) => {
    const nGrad = 10 * a + 5;
    const iB = 10 * a + b;
    const yAxe = 62;
    const xDe = (i) => 30 + (i * 620) / (nGrad + 1);
    let c = trait(xDe(0), yAxe, xDe(nGrad + 0.7), yAxe);
    for (let i = 0; i <= nGrad; i += 1) {
        const repere = i % 5 === 0;
        let couleur = ENCRE;
        if (i === iB) couleur = '#4ade80';
        if (repere) couleur = VERT;
        const h = repere ? 10 : 6;
        c += trait(xDe(i), yAxe - h, xDe(i), yAxe + h, couleur, repere ? 2.6 : 1.3);
    }
    const etiquette = { taille: 18, gras: true, couleur: VERT };
    c += texteSvg(xDe(0), yAxe + 32, '0', etiquette) + texteSvg(xDe(10), yAxe + 32, '1', etiquette)
        + texteSvg(xDe(iB), yAxe - 18, 'B', etiquette);
    return figure(680, 104, 'Demi-droite graduée où sont écrits 0 et 1, avec le point B sur une graduation.', c);
};
const genQ07 = () => {
    const a = randInt(2, 4);
    const b = randInt(1, 4);
    const juste = `${a},${b}`;
    const choix = [juste, `1,${10 * (a - 1) + b}`, String(10 * a + b), `${10 * a},${b}`];
    return makeQcm(choix, juste,
        '<p>Observer cette demi-droite graduée.</p>{{element:0}}<p>Quelle est l\'abscisse du point B ?</p>',
        avecFigure(L07, figureQ07(a, b)));
};

/**
 * Q08 — Repérage sur une droite graduée (fractions).
 * Item : « Quelle fraction correspond au point A ? » ; droite de 0 à 1 partagée en dix, prolongée d'une
 *   graduation, seuls 0 et 1 écrits ; A sur la 4e graduation, B (non demandé) sur la 2e ; 4/10 (juste),
 *   1/4, 4/1, 10/4.
 * Domaine : A sur la k-ième graduation, k de 2 à 9 sauf 5 (pour k = 1, 1/1 serait deux fois le même
 *   choix ; 5, le milieu de l'unité, se repère d'un coup d'œil) ; B sur une autre graduation, de 1 à 9.
 * Erreurs : la 4e graduation lue « un quart » (1/4) ; le nombre de graduations sur 1, l'unité écrite
 *   (4/1) ; numérateur et dénominateur échangés (10/4).
 */
const L08 = 'Repérage sur une droite graduée (fractions)';
const figureQ08 = (k, j) => {
    const yAxe = 58;
    const xDe = (i) => 36 + i * 58;
    let c = trait(xDe(0), yAxe, xDe(11) + 20, yAxe);
    for (let i = 0; i <= 11; i += 1) {
        const vert = [0, 10, k, j].includes(i);
        c += trait(xDe(i), yAxe - 8, xDe(i), yAxe + 8, vert ? VERT : ENCRE, vert ? 2.2 : 1.4);
    }
    const etiquette = { taille: 18, gras: true, couleur: VERT };
    c += texteSvg(xDe(0), yAxe + 32, '0', etiquette) + texteSvg(xDe(10), yAxe + 32, '1', etiquette)
        + texteSvg(xDe(k), yAxe - 16, 'A', etiquette) + texteSvg(xDe(j), yAxe - 16, 'B', etiquette);
    return figure(xDe(11) + 40, 100, 'Droite graduée où sont écrits 0 et 1, avec les points A et B.', c);
};
const genQ08 = () => {
    const k = randomChoice([2, 3, 4, 6, 7, 8, 9]);
    const j = randomChoice([1, 2, 3, 4, 5, 6, 7, 8, 9].filter((v) => v !== k));
    const juste = fraction(k, 10);
    const choix = [fraction(1, k), fraction(k, 1), juste, fraction(10, k)];
    return makeQcm(choix, juste, '<p>Quelle fraction correspond au point A ?</p>{{element:0}}', avecFigure(L08, figureQ08(k, j)));
};

/**
 * Q09 — Comparaison de nombres entiers.
 * Item : « Choisir le nombre encadré par 10 000 et 100 000 : 10 000 < … < 100 000 » ; 87 842 (juste),
 *   9 345, 235 412, 1 010 000.
 * Domaine : les bornes de l'item ; le nombre juste a cinq chiffres et commence par 6, 7 ou 8 (un
 *   premier chiffre plus grand que celui des bornes, comme 8) ; 9 suivi de trois chiffres pour le
 *   nombre de quatre chiffres, 2 ou 3 suivi de cinq chiffres pour celui de six.
 * Erreurs : le premier chiffre comparé sans compter les chiffres, 9 > 1 (9 345) ; six chiffres comme
 *   100 000, pris pour un nombre de l'encadrement (235 412) ; 1 010 000 : erreur non identifiée avec
 *   certitude, TRANCHÉ LE 10/10 (on garde) (un nombre écrit avec les chiffres des bornes, 1 puis 010 000), gardé tel quel.
 */
const L09 = 'Comparaison de nombres entiers';
const genQ09 = () => {
    const juste = randInt(6, 8) * 10000 + randInt(1000, 9999);
    const faux = [9000 + randInt(101, 999), randInt(2, 3) * 100000 + randInt(10000, 99999), 1010000];
    garde(juste > 10000 && juste < 100000 && faux.every((v) => v < 10000 || v > 100000), 'Q09 : encadrement');
    return makeQcm([juste, ...faux].map(formatNumber), formatNumber(juste),
        `<p>Choisir le nombre encadré par 10 000 et 100 000 :</p><p>${tex('10\\,000 \\lt \\dots \\lt 100\\,000')}</p>`,
        opts(L09));
};

/**
 * Q10 — Comparaison de nombres décimaux.
 * Item : « Quel est le plus petit nombre parmi les suivants ? » ; 1,082 (juste), 1,4, 1,09, 2,03.
 * Domaine : e,0ab (juste), e,d, e,0c et (e + 1),0f, avec c = a + 1 (des centièmes voisins, 8 et 9)
 *   et f < d < c (3 < 4 < 9) ; e de 1 à 8.
 * Erreurs : la partie décimale lue comme un entier, à partie entière égale (4 < 9 < 82 : 1,4) ; les
 *   dixièmes comparés, puis la suite lue comme un entier (9 < 82 : 1,09) ; la partie entière oubliée,
 *   03 = 3 étant la plus petite partie décimale (2,03).
 */
const L10 = 'Comparaison de nombres décimaux';
const genQ10 = () => {
    const e = randInt(1, 8);
    const a = randInt(2, 8);
    const c = a + 1;
    const b = randInt(1, 9);
    const d = randInt(2, c - 1);
    const f = randInt(1, d - 1);
    const juste = `${e},0${a}${b}`;
    const choix = [juste, `${e},${d}`, `${e},0${c}`, `${e + 1},0${f}`];
    garde(choix.filter((s) => valeur(s) <= valeur(juste)).length === 1, 'Q10 : un seul plus petit');
    return makeQcm(choix, juste, '<p>Quel est le plus petit nombre parmi les suivants ?</p>', opts(L10));
};

/**
 * Q11 — Partage : fractions.
 * Item : rectangle partagé en 7 bandes égales, les 3 premières coloriées en jaune ; « On a colorié
 *   les… » ; 3/7 de la figure. (juste), 3/4 de la figure., 4/4 de la figure., 4/7 de la figure.
 * Domaine : n bandes dont k coloriées, avec k < n − k (moins de bandes coloriées que de blanches, comme
 *   3 et 4), n de 5 à 10, k de 2 à 4.
 * Erreurs : les coloriées sur les blanches, une partie sur l'autre (3/4) ; la partie blanche (4/7) ;
 *   4/4 : erreur non identifiée, TRANCHÉ LE 10/10 (on garde), reproduite en blanches sur blanches.
 */
const L11 = 'Partage : fractions';
/** [bandes, coloriées] : moins de bandes coloriées que de blanches, comme 3 sur 7. */
const PARTAGES_Q11 = [[5, 2], [6, 2], [7, 2], [7, 3], [8, 2], [8, 3], [9, 2], [9, 3], [9, 4], [10, 2], [10, 3], [10, 4]];
const figureQ11 = (n, k) => {
    const w = 300 / n;
    let c = '';
    for (let i = 0; i < n; i += 1) {
        c += `<rect x="${px(10 + i * w)}" y="10" width="${px(w)}" height="220" fill="${i < k ? '#f2ea3c' : '#ffffff'}"`
            + ` stroke="${ENCRE}" stroke-width="3"/>`;
    }
    return figure(320, 240, 'Rectangle partagé en bandes de même largeur, certaines coloriées en jaune.', c);
};
const genQ11 = () => {
    const [n, k] = randomChoice(PARTAGES_Q11);
    const blanches = n - k;
    const part = (num, den) => `${fraction(num, den)} de la figure.`;
    const juste = part(k, n);
    const choix = [part(k, blanches), juste, part(blanches, blanches), part(blanches, n)];
    return makeQcm(choix, juste, '{{element:0}}<p>On a colorié les…</p>', avecFigure(L11, figureQ11(n, k)));
};

/**
 * Q12 — Calcul mental : opération « à trou », complément à la dizaine supérieure.
 * Item : « Quel nombre permet de compléter l'égalité pour qu'elle soit vraie ? 433 + … = 440 » ;
 *   7 (juste), 8, 6, 5.
 * Domaine : n à trois chiffres, unités de 2 à 7, dizaine de 1 à 8 (la dizaine supérieure ne change
 *   pas de centaine, comme 433 → 440) ; réponse 10 − unités, de 3 à 8.
 * Erreurs : un de trop (+1), un de moins (−1), deux de moins (−2), comme l'item.
 */
const L12 = 'Calcul mental : opération « à trou », complément à la dizaine';
const genQ12 = () => {
    const centaines = randInt(1, 9);
    const dizaines = randInt(1, 8);
    const unites = randInt(2, 7);
    const n = centaines * 100 + dizaines * 10 + unites;
    const cible = n - unites + 10;
    const juste = 10 - unites;
    const choix = [juste, juste + 1, juste - 1, juste - 2].map(String);
    return makeQcm(choix, String(juste),
        `<p>Quel nombre permet de compléter l'égalité pour qu'elle soit vraie ?</p><p>${n} + … = ${cible}</p>`, opts(L12));
};

/**
 * Q13 — Calcul mental : moitié d'un nombre décimal.
 * Item : « La moitié de 8,10 est … » ; 4,05 (juste), 4,20, 4,50, 4,5.
 * Domaine : a,10 avec a pair. La partie décimale reste « 10 » : c'est elle qui porte les trois erreurs
 *   (sa moitié, 5, doit s'écrire 05). La partie entière va de 2 à 28 (8 dans l'item ; de 2 à 8, il n'y
 *   aurait que 4 variantes), mode auto, TRANCHÉ LE 10/10 (on garde).
 * Erreurs : la partie décimale doublée au lieu d'être partagée, 10 → 20 (4,20) ; la moitié de 10, 5,
 *   écrite sur deux chiffres comme 10 (4,50) ; ou écrite juste après la virgule (4,5).
 */
const L13 = 'Calcul mental : moitié d\'un nombre décimal';
const genQ13 = () => {
    const a = 2 * randInt(1, 14);
    const m = a / 2;
    const juste = `${m},05`;
    garde(Math.abs(2 * valeur(juste) - valeur(`${a},10`)) < 1e-9, 'Q13 : moitié');
    const choix = [`${m},20`, `${m},50`, juste, `${m},5`];
    return makeQcm(choix, juste, `<p>La moitié de ${a},10 est …</p>`, opts(L13));
};

/**
 * Q14 — Calcul mental : multiplication d'un décimal par 10.
 * Item : « Calculer mentalement la multiplication suivante : 754,82 × 10 = … » ; 7 548,2 (juste),
 *   7,5482, 75,482, 75 482.
 * Domaine : abc,de × 10, trois chiffres avant la virgule et deux après, tous de 1 à 9 comme l'item.
 * Erreurs : la virgule déplacée de deux rangs vers la gauche (7,5482) ; d'un rang, mais vers la gauche
 *   (75,482) ; de deux rangs vers la droite, la virgule effacée (75 482).
 */
const L14 = 'Calcul mental : multiplication d\'un décimal par 10';
const genQ14 = () => {
    const [a, b, c, d, e] = [1, 2, 3, 4, 5].map(() => randInt(1, 9));
    const juste = `${formatNumber(a * 1000 + b * 100 + c * 10 + d)},${e}`;
    garde(Math.abs(valeur(juste) - valeur(`${a}${b}${c},${d}${e}`) * 10) < 1e-6, 'Q14 : produit');
    const choix = [`${a},${b}${c}${d}${e}`, `${a}${b},${c}${d}${e}`, juste, formatNumber(a * 10000 + b * 1000 + c * 100 + d * 10 + e)];
    return makeQcm(choix, juste,
        `<p>Calculer mentalement la multiplication suivante :</p><p>${a}${b}${c},${d}${e} × 10 = …</p>`, opts(L14));
};

/**
 * Q15 — Calcul mental : multiplication d'un décimal par 5.
 * Item : « Voici un calcul : 3,4 × 5 = Quel est le résultat de ce calcul ? » ; 17 (juste), 1,7, 15,2,
 *   152,0.
 * Domaine : a,b × 5 avec a de 2 à 9 et b pair (2, 4, 6 ou 8) : le produit est un entier de deux
 *   chiffres, comme 17, et b × 5 tombe sur une dizaine, comme 4 × 5 = 20.
 * Erreurs : la virgule placée dans 17 pour garder la décimale de 3,4 (1,7) ; 3 × 5 = 15 et 4 × 5 = 20
 *   juxtaposés de part et d'autre de la virgule, 15,20 écrit 15,2 ; les mêmes produits posés côte à
 *   côte sans retenue, 1520, puis une décimale (152,0).
 */
const L15 = 'Calcul mental : multiplication d\'un décimal par 5';
const genQ15 = () => {
    const a = randInt(2, 9);
    const b = randomChoice([2, 4, 6, 8]);
    const produit = 5 * a + b / 2;
    garde(Math.abs(valeur(`${a},${b}`) * 5 - produit) < 1e-9, 'Q15 : produit');
    const juste = String(produit);
    const choix = [`${Math.floor(produit / 10)},${produit % 10}`, `${5 * a},${b / 2}`, juste, `${5 * a}${b / 2},0`];
    return makeQcm(choix, juste,
        `<p>Voici un calcul :</p><p>${a},${b} × 5 =</p><p>Quel est le résultat de ce calcul ?</p>`, opts(L15));
};

/**
 * Q16 — Ordre de grandeur (longueur).
 * Item : « Compléter avec l'unité qui convient. Une coccinelle peut mesurer 7… » ; mm (juste), cm, m, dm.
 * Domaine : un petit animal ou un petit objet familier qui se mesure en millimètres, avec un nombre
 *   d'un chiffre ; la bonne unité reste le millimètre, et chaque taille est impossible en centimètres.
 * Erreurs : l'ordre de grandeur de l'objet n'est pas mobilisé : cm, l'unité de la règle, prise par
 *   habitude (10 fois trop grande) ; dm (100 fois) ; m (1 000 fois).
 */
const L16 = 'Ordre de grandeur';
/** Ce qui se mesure en millimètres, avec ses tailles plausibles. */
const PETITS_Q16 = [
    ['Une coccinelle', [5, 6, 7, 8]],
    ['Un grain de riz', [5, 6, 7]],
    ['Une puce', [2, 3]],
    ['Un pépin de pomme', [7, 8]],
    ['Un pépin de raisin', [5, 6]],
    ['Un petit pois', [7, 8, 9]],
    ['Un grain de blé', [6, 7]],
    ['Un grain de poivre', [4, 5]],
    ['Une graine de sésame', [3]],
];
const genQ16 = () => {
    const [sujet, tailles] = randomChoice(PETITS_Q16);
    return makeQcm(['mm', 'cm', 'm', 'dm'], 'mm',
        `<p>Compléter avec l'unité qui convient.</p><p>${sujet} peut mesurer ${randomChoice(tailles)}…</p>`, opts(L16));
};

/**
 * Q17 — Périmètre d'un rectangle.
 * Item : rectangle de 4 carreaux sur 7, debout, sur un quadrillage dont chaque carreau mesure 1 cm ;
 *   « Quel est le périmètre, en centimètre, de ce rectangle ? » ; 22 cm (juste), 14 cm, 18 cm, 28 cm.
 * Domaine : 4 carreaux sur L, L de 5 à 10, debout ou couché. La largeur reste 4 : c'est la seule où
 *   les deux lectures de 18 donnent le même nombre.
 * Erreurs : l'aire au lieu du périmètre (28) ; les carreaux du bord comptés au lieu des côtés,
 *   2 × 7 + 2 × 2, qui valent aussi trois côtés, 7 + 4 + 7 (18) ; 14 : erreur non identifiée avec
 *   certitude, TRANCHÉ LE 10/10 (on garde), reproduite en deux longueurs seulement (7 + 7).
 */
const L17 = 'Périmètre d\'un rectangle';
const GRILLE = '#b8bec8';
const figureQ17 = (largeur, hauteur) => {
    const u = 26;
    const bord = 12;
    const W = (largeur + 2) * u + 2 * bord;
    const H = (hauteur + 2) * u + 2 * bord;
    let c = '';
    for (let i = 0; i <= largeur + 2; i += 1) {
        c += trait(bord + i * u, 0, bord + i * u, H, GRILLE, 1);
    }
    for (let j = 0; j <= hauteur + 2; j += 1) {
        c += trait(0, bord + j * u, W, bord + j * u, GRILLE, 1);
    }
    c += `<rect x="${bord + u}" y="${bord + u}" width="${largeur * u}" height="${hauteur * u}" fill="none"`
        + ` stroke="${ENCRE}" stroke-width="3"/>`;
    return figure(W, H, 'Rectangle tracé sur un quadrillage.', c);
};
const genQ17 = () => {
    const longueur = randInt(5, 10);
    const largeur = 4;
    const juste = `${2 * (longueur + largeur)} cm`;
    const choix = [`${2 * longueur} cm`, `${2 * longueur + 2 * largeur - 4} cm`, juste, `${longueur * largeur} cm`];
    const debout = randomChoice([true, false]);
    return makeQcm(choix, juste,
        '<p>Un rectangle est dessiné sur un quadrillage. Dans ce quadrillage, chaque carreau mesure 1 cm de côté.</p>'
        + '{{element:0}}<p>Quel est le périmètre, en centimètre, de ce rectangle ?</p>',
        avecFigure(L17, debout ? figureQ17(largeur, longueur) : figureQ17(longueur, largeur)));
};

/**
 * Q18 — Unités, comparaison.
 * Item : « Le professeur demande à un élève : « 1 m c'est combien de fois plus grand que 1 dm ? » » ;
 *   10 fois (juste), 100 fois, 1 000 fois, 10 000 fois. La capture dit « 1 m », la base « 1 cm ».
 * Domaine : deux unités voisines du tableau, la plus grande d'abord ; la réponse reste 10 fois. Les
 *   longueurs n'en donnent que 6 paires : les masses (kg à mg) et les contenances (L à mL) sont
 *   ajoutées, mode auto, TRANCHÉ LE 10/10 (on garde).
 * Erreurs : un rang de trop compté dans le tableau, le décimètre pris pour le centimètre (100 fois) ;
 *   deux rangs de trop, pris pour le millimètre (1 000 fois) ; 10 000 fois : trois rangs de trop,
 *   aucune unité usuelle, erreur non identifiée, TRANCHÉ LE 10/10 (on garde).
 */
const L18 = 'Unités, comparaison';
/** Deux unités voisines du tableau, la plus grande d'abord. */
const VOISINES_Q18 = [
    ['km', 'hm'], ['hm', 'dam'], ['dam', 'm'], ['m', 'dm'], ['dm', 'cm'], ['cm', 'mm'],
    ['kg', 'hg'], ['hg', 'dag'], ['dag', 'g'], ['g', 'dg'], ['dg', 'cg'], ['cg', 'mg'],
    ['L', 'dL'], ['dL', 'cL'], ['cL', 'mL'],
];
const genQ18 = () => {
    const [grande, petite] = randomChoice(VOISINES_Q18);
    return makeQcm(['10 fois', '100 fois', '1 000 fois', '10 000 fois'], '10 fois',
        `<p>Le professeur demande à un élève : « 1 ${grande} c'est combien de fois plus grand que 1 ${petite} ? »</p>`, opts(L18));
};

/**
 * Q19 — Conversion de longueurs.
 * Item : « Compléter l'égalité : 24 cm = … mm » ; 240 (juste), 0,24, 2,4, 2 400.
 * Domaine : n cm = … mm, n de deux chiffres, jamais un multiple de 10 (2,4 et 0,24 garderaient sinon
 *   un zéro inutile).
 * Erreurs : divisé par 100, la conversion en mètres (0,24) ; divisé par 10, le mauvais sens (2,4) ;
 *   multiplié par 100, un rang de trop (2 400).
 */
const L19 = 'Conversion de longueurs';
const genQ19 = () => {
    const n = randInt(1, 9) * 10 + randInt(1, 9);
    const juste = String(n * 10);
    const choix = [`0,${n}`, `${Math.floor(n / 10)},${n % 10}`, juste, formatNumber(n * 100)];
    return makeQcm(choix, juste, `<p>Compléter l'égalité : ${n} cm = … mm</p>`, opts(L19));
};

/**
 * Q20 — Aire d'un carré.
 * Item : carré de 3 carreaux de côté sur un quadrillage en pointillés, chaque carreau de 1 cm ;
 *   « Quelle est l'aire de ce carré en cm² ? » ; 9 cm² (juste), 6 cm², 12 cm², 15 cm².
 * Domaine : un côté c de 3, 5, 6 ou 7 carreaux (2 et 4 exclus : l'aire y vaut 2 × c ou le
 *   périmètre) ; le carré change aussi de place sur le quadrillage. Quatre côtés seulement,
 *   TRANCHÉ LE 10/10 (on garde).
 * Erreurs : côté + côté, ou 3² lu 3 × 2 (6) ; le périmètre (12) ; 15 : erreur non identifiée,
 *   TRANCHÉ LE 10/10 (on garde), reproduite en c × (c + 2), qui vaut 15 pour 3 et ne tombe jamais sur l'aire.
 */
const L20 = 'Aire d\'un carré';
const figureQ20 = (cote, [gauche, droite, haut, bas]) => {
    const u = Math.round(200 / cote);
    const W = (gauche + cote + droite) * u;
    const H = (haut + cote + bas) * u;
    const x0 = gauche * u;
    const y0 = haut * u;
    const pointilles = ' stroke-dasharray="7 5"';
    let c = `<rect x="0" y="0" width="${px(W)}" height="${px(H)}" fill="#b4b4b4"/>`
        + `<rect x="${px(x0)}" y="${px(y0)}" width="${cote * u}" height="${cote * u}" fill="#7f88c6"/>`;
    for (let i = gauche > 1 ? -1 : 0; i <= cote + (droite > 1 ? 1 : 0); i += 1) {
        c += trait(x0 + i * u, 0, x0 + i * u, H, '#1f2937', 1.2, pointilles);
    }
    for (let j = haut > 1 ? -1 : 0; j <= cote + (bas > 1 ? 1 : 0); j += 1) {
        c += trait(0, y0 + j * u, W, y0 + j * u, '#1f2937', 1.2, pointilles);
    }
    c += `<rect x="${px(x0)}" y="${px(y0)}" width="${cote * u}" height="${cote * u}" fill="none" stroke="#2457a6" stroke-width="4"/>`;
    return figure(W, H, 'Carré colorié sur un quadrillage.', c);
};
const genQ20 = () => {
    const cote = randomChoice([3, 5, 6, 7]);
    const marges = [1, 2, 3, 4].map(() => randomChoice([0.5, 1.5]));   // en carreaux, 0,5 comme l'item
    const juste = `${cote * cote} cm²`;
    const choix = [`${2 * cote} cm²`, juste, `${4 * cote} cm²`, `${cote * (cote + 2)} cm²`];
    return makeQcm(choix, juste,
        '<p>Un carré est dessiné sur un quadrillage. Chaque carreau de ce quadrillage fait 1 cm de côté.</p>'
        + '{{element:0}}<p>Quelle est l\'aire de ce carré en cm² ?</p>',
        avecFigure(L20, figureQ20(cote, marges)));
};

/**
 * Q21 — Volume d'un pavé droit par dénombrement.
 * Item : pavé droit d'une seule couche de 5 × 4 petits cubes de 1 cm³, en perspective (dessus bleu,
 *   face rouge de 5 cubes, face verte de 4) ; « Quel est le volume de ce pavé droit ? » ; 20 cm³ (juste),
 *   4 cm³, 9 cm³, 24 cm³.
 * Domaine : une couche de R × V cubes, R (face rouge) de 3 à 7, V (face verte) de 3 à 6, R ≠ V.
 * Erreurs : les cubes d'une seule arête, la face verte (4) ; les deux arêtes additionnées, ou les
 *   carrés des deux faces de côté comptés (5 + 4 = 9) ; 24 : erreur non identifiée, TRANCHÉ LE 10/10 (on garde),
 *   reproduite en V × (R + 1), le dessus et la face verte comptés (20 + 4).
 */
const L21 = 'Volume d\'un pavé droit par dénombrement';
const figureQ21 = (rouge, vert) => {
    // x le long de la face rouge (vers la droite), y le long de la face verte (vers la gauche), z en haut
    const projection = (x, y, z) => [x * 34 - y * 26, -x * 12 - y * 20 - z * 36];
    const coins = [];
    [0, rouge].forEach((x) => [0, vert].forEach((y) => [0, 1].forEach((z) => coins.push(projection(x, y, z)))));
    const xMin = Math.min(...coins.map((p) => p[0])) - 12;
    const yMin = Math.min(...coins.map((p) => p[1])) - 12;
    const W = Math.max(...coins.map((p) => p[0])) - xMin + 12;
    const H = Math.max(...coins.map((p) => p[1])) - yMin + 12;
    const P = (x, y, z) => {
        const [sx, sy] = projection(x, y, z);
        return [sx - xMin, sy - yMin];
    };
    const arete = (p, q, couleur) => trait(p[0], p[1], q[0], q[1], couleur, 1.2);
    let c = polygone([P(0, 0, 1), P(rouge, 0, 1), P(rouge, vert, 1), P(0, vert, 1)], '#6470c8', '#3a46a0')
        + polygone([P(0, 0, 0), P(rouge, 0, 0), P(rouge, 0, 1), P(0, 0, 1)], '#f1828a', '#c0434e')
        + polygone([P(0, 0, 0), P(0, vert, 0), P(0, vert, 1), P(0, 0, 1)], '#78cc97', '#2f8f55');
    for (let i = 1; i < rouge; i += 1) {
        c += arete(P(i, 0, 1), P(i, vert, 1), '#3a46a0') + arete(P(i, 0, 0), P(i, 0, 1), '#c0434e');
    }
    for (let j = 1; j < vert; j += 1) {
        c += arete(P(0, j, 1), P(rouge, j, 1), '#3a46a0') + arete(P(0, j, 0), P(0, j, 1), '#2f8f55');
    }
    return figure(W, H, 'Pavé droit formé de petits cubes.', c);
};
const genQ21 = () => {
    const rouge = randInt(3, 7);
    const vert = randomChoice([3, 4, 5, 6].filter((v) => v !== rouge));
    const juste = `${rouge * vert} cm³`;
    const choix = [`${vert} cm³`, `${rouge + vert} cm³`, juste, `${vert * (rouge + 1)} cm³`];
    return makeQcm(choix, juste,
        '<p>Le pavé droit ci-dessous est composé de petits cubes de 1 cm³.</p>{{element:0}}'
        + '<p>Quel est le volume de ce pavé droit ?</p>',
        avecFigure(L21, figureQ21(rouge, vert)));
};

/**
 * Q22 — Comparaison d'angles.
 * Item : « Louis a tracé deux angles \widehat{B} et \widehat{E}. Il souhaite les comparer. » ; sur un
 *   quadrillage, [BA] va de 4 carreaux vers la gauche et 2 vers le haut, [BC] de 3 carreaux ; [ED] de 2
 *   et 1, [EF] de 4 ; « Les angles B et E sont égaux. » (juste), « L'angle B est plus grand que l'angle
 *   E. », « L'angle B est plus petit que l'angle E. », « On ne peut pas le savoir. » (la capture : « On
 *   ne peut pas savoir. ») ; les angles portent leur chapeau, en KaTeX. « Il » devient « Louis […] et
 *   souhaite » : pas de pronom pour une personne nommée.
 * Domaine : deux angles de même mesure, côtés obliques de même direction (q carreaux à gauche, p vers
 *   le haut), l'un k fois plus long (k = 2 ou 3) avec le côté horizontal le plus court, l'autre
 *   l'inverse ; B ou E porte le côté oblique le plus long.
 * Erreurs : plus grand ou plus petit, l'angle jugé à la longueur d'un de ses côtés (le côté oblique,
 *   plus long pour B dans l'item, ou le côté horizontal, plus long pour E) ; « On ne peut pas le
 *   savoir. » : sans mesure, ou avec des côtés de longueurs différentes, la comparaison paraît impossible.
 */
const L22 = 'Comparaison d\'angles';
/** Le côté oblique va de q carreaux vers la gauche et p vers le haut ; l'un des deux est k fois plus long. */
const OBLIQUES_Q22 = [{ q: 2, p: 1, k: 2 }, { q: 2, p: 1, k: 3 }, { q: 3, p: 1, k: 2 }, { q: 3, p: 2, k: 2 }];
/** [côté horizontal de l'angle au long côté oblique, côté horizontal de l'autre] : le premier est le plus court. */
const HORIZONTAUX_Q22 = [[2, 3], [2, 4], [3, 4], [3, 5]];
const figureQ22 = (angleB, angleE, q, p) => {
    const u = 34;
    const largeur = ({ k, h }) => Math.max(k * q, h);
    const yBase = 1 + Math.max(angleB.k, angleE.k) * p;
    const xB = 1 + largeur(angleB);
    const xE = xB + 2 + largeur(angleE);
    const colonnes = xE + 1;
    const rangees = yBase + 1;
    let c = `<rect x="0" y="0" width="${colonnes * u}" height="${rangees * u}" fill="#e5e5e5"/>`;
    for (let i = 0; i <= colonnes; i += 1) {
        c += trait(i * u, 0, i * u, rangees * u, '#b5b5b5', 1);
    }
    for (let j = 0; j <= rangees; j += 1) {
        c += trait(0, j * u, colonnes * u, j * u, '#b5b5b5', 1);
    }
    const lettre = { taille: 13, gras: true };
    const angle = (xS, { k, h }, [nomOblique, nomSommet, nomHorizontal]) => {
        const [sx, sy] = [xS * u, yBase * u];
        const [ox, oy] = [(xS - k * q) * u, (yBase - k * p) * u];
        const hx = (xS - h) * u;
        const r = 0.6 * u;
        const n = Math.hypot(q, p);
        return trait(sx, sy, ox, oy, ENCRE, 1.4) + trait(sx, sy, hx, sy, ENCRE, 1.4)
            + `<path d="M ${px(sx - r)} ${px(sy)} A ${px(r)} ${px(r)} 0 0 1 ${px(sx - (r * q) / n)} ${px(sy - (r * p) / n)}"`
            + ` fill="none" stroke="${ENCRE}" stroke-width="1.3"/>`
            + texteSvg(ox, oy - 9, nomOblique, lettre) + texteSvg(sx + 4, sy + 19, nomSommet, lettre)
            + texteSvg(hx - 8, sy + 19, nomHorizontal, lettre);
    };
    c += angle(xB, angleB, ['A', 'B', 'C']) + angle(xE, angleE, ['D', 'E', 'F']);
    return figure(colonnes * u, rangees * u, 'Deux angles tracés sur un quadrillage, de sommets B et E.', c);
};
const genQ22 = () => {
    const { q, p, k } = randomChoice(OBLIQUES_Q22);
    const [hCourt, hLong] = randomChoice(HORIZONTAUX_Q22);
    const longOblique = { k, h: hCourt };
    const courtOblique = { k: 1, h: hLong };
    // Dans l'item, B porte le côté oblique le plus long ; une variante sur deux, c'est E.
    const [angleB, angleE] = randomChoice([[longOblique, courtOblique], [courtOblique, longOblique]]);
    // Second chemin : l'ouverture de chaque angle, calculée sur les deux côtés tracés.
    const ouverture = ({ k: m, h }) => Math.acos((m * q * h) / (Math.hypot(m * q, m * p) * h));
    garde(Math.abs(ouverture(angleB) - ouverture(angleE)) < 1e-9, 'Q22 : angles égaux');
    const B = tex('\\widehat{B}');
    const E = tex('\\widehat{E}');
    const juste = `Les angles ${B} et ${E} sont égaux.`;
    const choix = [juste, `L'angle ${B} est plus grand que l'angle ${E}.`, `L'angle ${B} est plus petit que l'angle ${E}.`,
        'On ne peut pas le savoir.'];
    return makeQcm(choix, juste, `<p>Louis a tracé deux angles ${B} et ${E} et souhaite les comparer.</p>{{element:0}}`,
        avecFigure(L22, figureQ22(angleB, angleE, q, p)));
};

/**
 * Q23 — Ordre de grandeur, masse.
 * Item : « Une balle de tennis de table pèse 2,7… » ; grammes (g). (juste), kilogrammes (kg).,
 *   millimètres (mm)., centilitres (cL).
 * Domaine : un objet léger de masse connue, en grammes (les pièces en euros, à leur masse officielle ;
 *   des balles) ; la bonne unité reste le gramme.
 * Erreurs : une unité de masse 1 000 fois trop grande (kg) ; une unité de longueur (mm) ; une unité de
 *   contenance (cL).
 */
const L23 = 'Ordre de grandeur, masse';
const MASSES_Q23 = [
    ['Une balle de tennis de table pèse', '2,7'],
    ['Une pièce de 1 centime pèse', '2,3'],
    ['Une pièce de 2 centimes pèse', '3,06'],
    ['Une pièce de 5 centimes pèse', '3,92'],
    ['Une pièce de 10 centimes pèse', '4,1'],
    ['Une pièce de 20 centimes pèse', '5,74'],
    ['Une pièce de 50 centimes pèse', '7,8'],
    ['Une pièce de 1 euro pèse', '7,5'],
    ['Une pièce de 2 euros pèse', '8,5'],
    ['Une balle de golf pèse environ', '46'],
    ['Une balle de tennis pèse environ', '57'],
];
const genQ23 = () => {
    const [sujet, masse] = randomChoice(MASSES_Q23);
    const choix = ['kilogrammes (kg).', 'grammes (g).', 'millimètres (mm).', 'centilitres (cL).'];
    return makeQcm(choix, 'grammes (g).', `<p>${sujet} ${masse}…</p>`, opts(L23));
};

const GENERATORS = {
    q01_qcm_unique: genQ01,
    q02_qcm_unique: genQ02,
    q03_qcm_unique: genQ03,
    q04_qcm_unique: genQ04,
    q05_qcm_unique: genQ05,
    q06_qcm_unique: genQ06,
    q07_qcm_unique: genQ07,
    q08_qcm_unique: genQ08,
    q09_qcm_unique: genQ09,
    q10_qcm_unique: genQ10,
    q11_qcm_unique: genQ11,
    q12_qcm_unique: genQ12,
    q13_qcm_unique: genQ13,
    q14_qcm_unique: genQ14,
    q15_qcm_unique: genQ15,
    q16_qcm_unique: genQ16,
    q17_qcm_unique: genQ17,
    q18_qcm_unique: genQ18,
    q19_qcm_unique: genQ19,
    q20_qcm_unique: genQ20,
    q21_qcm_unique: genQ21,
    q22_qcm_unique: genQ22,
    q23_qcm_unique: genQ23,
};

export const ITEMS = [
    { questionNum: 'Q01', formatKey: 'q01_qcm_unique', libelle: L01, bonneReponseOfficielle: '76 004' },
    { questionNum: 'Q02', formatKey: 'q02_qcm_unique', libelle: L02, bonneReponseOfficielle: '0,072' },
    { questionNum: 'Q03', formatKey: 'q03_qcm_unique', libelle: L03, bonneReponseOfficielle: '3,2' },
    { questionNum: 'Q04', formatKey: 'q04_qcm_unique', libelle: L04, bonneReponseOfficielle: '0,04' },
    { questionNum: 'Q05', formatKey: 'q05_qcm_unique', libelle: L05, bonneReponseOfficielle: '10 fois 5 millièmes.' },
    { questionNum: 'Q06', formatKey: 'q06_qcm_unique', libelle: L06, bonneReponseOfficielle: '2 600 000' },
    { questionNum: 'Q07', formatKey: 'q07_qcm_unique', libelle: L07, bonneReponseOfficielle: '2,2' },
    { questionNum: 'Q08', formatKey: 'q08_qcm_unique', libelle: L08, bonneReponseOfficielle: '4/10' },
    { questionNum: 'Q09', formatKey: 'q09_qcm_unique', libelle: L09, bonneReponseOfficielle: '87 842' },
    { questionNum: 'Q10', formatKey: 'q10_qcm_unique', libelle: L10, bonneReponseOfficielle: '1,082' },
    { questionNum: 'Q11', formatKey: 'q11_qcm_unique', libelle: L11, bonneReponseOfficielle: '3/7 de la figure.' },
    { questionNum: 'Q12', formatKey: 'q12_qcm_unique', libelle: L12, bonneReponseOfficielle: '7' },
    { questionNum: 'Q13', formatKey: 'q13_qcm_unique', libelle: L13, bonneReponseOfficielle: '4,05' },
    { questionNum: 'Q14', formatKey: 'q14_qcm_unique', libelle: L14, bonneReponseOfficielle: '7 548,2' },
    { questionNum: 'Q15', formatKey: 'q15_qcm_unique', libelle: L15, bonneReponseOfficielle: '17' },
    { questionNum: 'Q16', formatKey: 'q16_qcm_unique', libelle: L16, bonneReponseOfficielle: 'mm' },
    { questionNum: 'Q17', formatKey: 'q17_qcm_unique', libelle: L17, bonneReponseOfficielle: '22 cm' },
    { questionNum: 'Q18', formatKey: 'q18_qcm_unique', libelle: L18, bonneReponseOfficielle: '10 fois' },
    { questionNum: 'Q19', formatKey: 'q19_qcm_unique', libelle: L19, bonneReponseOfficielle: '240' },
    { questionNum: 'Q20', formatKey: 'q20_qcm_unique', libelle: L20, bonneReponseOfficielle: '9 cm²' },
    { questionNum: 'Q21', formatKey: 'q21_qcm_unique', libelle: L21, bonneReponseOfficielle: '20 cm³' },
    { questionNum: 'Q22', formatKey: 'q22_qcm_unique', libelle: L22, bonneReponseOfficielle: 'Les angles B et E sont égaux.' },
    { questionNum: 'Q23', formatKey: 'q23_qcm_unique', libelle: L23, bonneReponseOfficielle: 'grammes (g).' },
];

export const CATEGORIES = ITEMS.map(({ questionNum, formatKey, libelle }) => ({
    id: questionNum.toLowerCase(),
    label: `${questionNum} · ${libelle}`,
    formatsList: [{ key: formatKey, label: 'QCM' }],
}));

export const generate = (typeExercice) => {
    const key = GENERATORS[typeExercice] ? typeExercice : randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
